import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styles from './CoverContent.module.scss';
import { ClockCircleOutlined } from '@ant-design/icons';
import { formatDate } from '@src/utils/time';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { 
  IconSkill,
  IconExercises,
  IconComforts,
  IconMoods,
  IconStructures,
  IconStandalone,
  categoryStructureOptions,
  categoryComfortLevelOptions,
  categoryMoodOptions1,
  categoryExerciseStyleOptions,
  IconLesson,
 } from '@src/constants';
import { AusCourseContent, CourseContentType } from '@src/api/graphql-codegen';
import { getMediaAbsolutePath } from '@src/services/File';
import { useQueryDictionary } from '@src/hooks';
import ImageGrade from '@src/assets/img/grade_hat.png';
import { useMediaQuery } from 'react-responsive';
import useDragScrolling from '../customize-units/card/useDragScrolling';
import { useDrag } from 'react-dnd';
import IconRemovUnit from 'src/assets/icons/remove_unit.svg?react';
// import Tooltip from '../tooltip/Tooltip';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Tooltip } from "antd";
import CompleteIcon from 'src/assets/icons/select_mark.svg?react';
import ImageBackground from '@src/assets/img/unitbackground.png';
import type { DraggableProvided, DraggableStateSnapshot } from '@src/utils/componentCasts';
import { DragDropContext, Droppable, Draggable } from '@src/utils/componentCasts';

type Props = {
  content: AusCourseContent;
  type?: string;
  source?: string;
  target?: string;
  handleOpenContent?: (content: AusCourseContent) => void;
  handleLeftArrowAction?: (content: AusCourseContent) => void;
  handleRightArrowAction?: (content: AusCourseContent) => void;
  programID?: string | "";
  handleMovableNewCover?: (inNew: boolean) => void;
  draggableProvided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
}

function CoverContent(props: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const { content, type="type", source, programID, handleOpenContent, handleRightArrowAction, handleMovableNewCover, draggableProvided} = props;
  const programOptions = useQueryDictionary({ dictionary: 'Programs' });
  const isMobile = useMediaQuery({ maxWidth: 915 });
  const { id, name, subHeading, categories, isNewCover, suggestedLessonCount, suggestedDuration, progress, coverImage, isComingSoon, comingSoonAt, isNew, isUpdated, type: contentType,
    newLabelPosition, comingSoonLabelPosition, updatedLabelPosition } = content;
  const isUnit = contentType === CourseContentType.UNIT;
  const unitCategories = useMemo(() => JSON.parse(categories || '[]'), [categories]);
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();

  const [{ opacity, isDragging }, drag] = useDrag(
    () => ({
      type,
      item: () => {
        // like begin drag
        addEventListenerForWindow();
        return { content }
      },
      end: () => {
        // like end drag
        removeEventListenerForWindow();
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
        opacity: !!monitor.isDragging() ? 0.4 : 1,
      }),
    }),
    [content, type],
  )

  const [structure, setStructure] = useState('')
  const [skill, setSkill] = useState('')
  const [comfortLevel, setComfortLevel] = useState('')
  const [mood, setMood] = useState('')
  const [exerciseStyle, setExerciseStyle] = useState('')

  useEffect(() => {
    if (isDragging) {
      handleMovableNewCover?.(isNewCover ? isNewCover : false)
    }
  }, [isDragging, isNewCover, handleMovableNewCover])

  useEffect(() => {
    if (unitCategories['Structure']) {
      setStructure(unitCategories['Structure'])
    }
    if (unitCategories['Skill']) {
      setSkill(unitCategories['Skill'])
    }
    if (unitCategories['Comfort Level']) {
      setComfortLevel(unitCategories['Comfort Level'])
    }
    if (unitCategories['Mood']) {
      setMood(unitCategories['Mood'])
    }
    if (unitCategories['Exercise Style']) {
      setExerciseStyle(unitCategories['Exercise Style'])
    }
  }, [unitCategories])

  const comingSoonDate = useMemo(() => {
    if (isComingSoon && comingSoonAt) {
      let d;
      if (comingSoonAt.includes('-')) {
        d = new Date('2023-09-23');
      } else {
        d = new Date(parseInt(comingSoonAt));
      }
      var dateString = formatDate(d.valueOf(), 'MMM dd, yyyy')
      return dateString;
    } else {
      return ''
    }
  }, [isComingSoon, comingSoonAt])

  const onClickUnit = useCallback(() => {
    handleOpenContent?.(content)
  }, [handleOpenContent, content])

  const actionContent = useMemo(() => {
    switch (source) {
      case 'grade': {
        return <div className={styles.iconWrapper}>
          <Tooltip placement="top" title={'Remove Content'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(content)} className={styles.arrowLeftIcon}/>
          </Tooltip>    
        </div>
      }
      case 'addition': {
        return <div className={styles.iconWrapper}>
          <Tooltip placement="top" title={'Remove Content'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(content)} className={styles.arrowLeftIcon}/>
          </Tooltip>
        </div>
      }
      default: {
        return <>
        </>
      }
    }
  }, [content, source, handleRightArrowAction]);

  const label = useMemo(() => {
    if (isNew) {
      return (
        <div className={styles.labelContainer}>NEW</div>
      )
    } else if (isUpdated) {
      return (
        <div className={styles.labelContainer}>UPDATED</div>
      )
    } else if (isComingSoon) {
      return (
        <div className={styles.labelContainer}>COMING SOON {comingSoonDate}</div>
      )
    }
  }, [isNew, isUpdated, isComingSoon, comingSoonDate])

  const newLabelStyle = useMemo(() => {
    const classes = [styles.oldLabelContainer];
    if (newLabelPosition === 'tl') {
      classes.push(styles.tlComingSoonLabel);
    } else if (newLabelPosition === 'tr') {
      classes.push(styles.trComingSoonLabel);
    } else if (newLabelPosition === 'bl') {
      classes.push(styles.blComingSoonLabel);
    } else if (newLabelPosition === 'br') {
      classes.push(styles.brComingSoonLabel);
    }
    return classes.join(' ');
  },[newLabelPosition])

  const updatedLabelStyle = useMemo(() => {
    const classes = [styles.oldLabelContainer];
    if (updatedLabelPosition === 'tl') {
      classes.push(styles.tlComingSoonLabel);
    } else if (updatedLabelPosition === 'tr') {
      classes.push(styles.trComingSoonLabel);
    } else if (updatedLabelPosition === 'bl') {
      classes.push(styles.blComingSoonLabel);
    } else if (updatedLabelPosition === 'br') {
      classes.push(styles.brComingSoonLabel);
    }
    return classes.join(' ');
  },[updatedLabelPosition])

  const comingSoonLabelStyle = useMemo(() => {
    const classes = [styles.oldLabelContainer];
    if (comingSoonLabelPosition === 'tl') {
      classes.push(styles.tlComingSoonLabel);
    } else if (comingSoonLabelPosition === 'tr') {
      classes.push(styles.trComingSoonLabel);
    } else if (comingSoonLabelPosition === 'bl') {
      classes.push(styles.blComingSoonLabel);
    } else if (comingSoonLabelPosition === 'br') {
      classes.push(styles.brComingSoonLabel);
    }
    return classes.join(' ');
  },[comingSoonLabelPosition])

  const oldLabel = useMemo(() => {
    if (isNew) {
      return (
        <div className={newLabelStyle}>NEW</div>
      )
    } else if (isUpdated) {
      return (
        <div className={updatedLabelStyle}>UPDATED</div>
      )
    } else if (isComingSoon) {
      return (
        <div className={comingSoonLabelStyle}>COMING SOON {comingSoonDate}</div>
      )
    }
  }, [isNew, isUpdated, isComingSoon, newLabelStyle, updatedLabelStyle, comingSoonLabelStyle, comingSoonDate])

  const gradeLabel = useMemo(() => {
    return (programOptions.find(p => p.value === programID)?.label ?? "").replace(' Program', '')
  }, [programID, programOptions])

  const completedLabel = useMemo(() => {
    if (progress === 100) {
      return (
        <div className={styles.completeLabel}>
          <div>
            <div className={styles.completeIcon}>
              <CompleteIcon width={10} height={10}/>
            </div>
            <span>{'Completed'}</span>
          </div>
        </div>
      )  
    }
    return null
  }, [progress])

  const gradeBar = (
    <div className={styles.gradeBar}>
      <img src={ImageGrade} alt='grade' />
      <span>{gradeLabel}</span>
    </div>
  )

  const progressBar = (
    <div className={styles.progressBackBar} style={{borderBottomLeftRadius: isNewCover ? '0' : '3px', borderBottomRightRadius: isNewCover ? '0' : '3px'}}>
      <div className={styles.progressBar} style={{width: `${progress}%`, backgroundColor: progress === 100 ? "#62C590" : "#62C590", borderBottomLeftRadius: isNewCover ? '0' : '3px', borderBottomRightRadius: isNewCover ? '0' : progress === 100 ? '3px' : '0' }}>
      </div>
    </div>
  )

  const suggestBar = (
    <div className={isUnit ? styles.unitSuggestView : styles.lessonSuggestView}>
      <div className={styles.lessonsView}>
        <LordIcon src={isUnit ? IconStandalone : IconLesson} isHover={true} colors={{ primary: "#FFF", secondary: "#FFF" }} size={isUnit ? 20 : 16}/>
        <div className={styles.lessonsText}>{suggestedLessonCount && suggestedLessonCount > 1 ? `${suggestedLessonCount} Lessons` : `${suggestedLessonCount} Lesson`}</div>
      </div>
      <div className={styles.durationView}>
        <ClockCircleOutlined className={styles.clockIcon}/>
        <div className={styles.durationText}>{`${suggestedLessonCount}`}&nbsp;<span>x</span>&nbsp;{`${suggestedDuration}`}&nbsp;<span>min</span></div>
      </div>
    </div>
  )

  const bottomView = (
    <div className={styles.bottomView}>
      <div className={styles.unitName}>{name}</div>
      <div className={styles.subHeading}>{subHeading}</div>
      {skill ? <div className={[styles.tagBox, styles['skill']].join(' ')}>
        <LordIcon src={IconSkill}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
        <div className={styles.title}>Skill: </div><span>{skill}</span>
      </div> : null}
      <div className={styles.divider} />
      <div className={styles.categoryView}>
      {mood ? <div className={[styles.tagBox, styles['mood']].join(' ')}>
        <LordIcon src={IconMoods[categoryMoodOptions1.findIndex(o => o.value === mood)]}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
          <span>{mood}</span></div> : null}
      {comfortLevel ? <div className={[styles.tagBox, styles['comfortLevel']].join(' ')}>
        <LordIcon src={IconComforts[categoryComfortLevelOptions.findIndex(o => o.value === comfortLevel)]}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
        <span>{comfortLevel}</span></div> : null}
      {structure ? <div className={[styles.tagBox, styles['structure']].join(' ')}>
        <LordIcon src={IconStructures[categoryStructureOptions.findIndex(o => o.value === structure)]}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
        <span>{structure}</span></div> : null}
      {exerciseStyle ? <div className={[styles.tagBox, styles['exerciseStyle']].join(' ')}>
        <LordIcon src={IconExercises[categoryExerciseStyleOptions.findIndex(o => o.value === exerciseStyle)]}
          isHover={true} 
          colors={{
            primary: '#274649',
            secondary: '#FFB073'
          }}
          size={isMobile ? 10: 16}
          target='.CoverUnit_tagBox__nusN2' />
          <span>{exerciseStyle}</span></div> : null}
      </div>
    </div>
  )

  return (
    <div ref={drag} id={`${programID}_${id}`} className={[isHovered ? styles.newCoverHoveredCard : styles.newCoverCard, source === 'grade' ? styles.innerShadow : ''].join(' ')} 
      style={{height: !isNewCover ? 'min-content' : '', opacity, cursor: isDragging ? 'grabbing' : 'grab', borderRadius: isNewCover ? '10px' : '3px'}} {...draggableProvided?.dragHandleProps}
      onClick={onClickUnit} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className={styles.topView}>
        {isUnit && isNewCover ? <div className={styles.unitBackground}>
          <img src={ImageBackground} alt='background'/>
          <div className={styles.unitImage}
            style={{backgroundImage: `url(${coverImage ? getMediaAbsolutePath(coverImage) : ''})`}}>
              {completedLabel}
          </div>
        </div> : <>
          <LazyLoadImage src={`${coverImage ? getMediaAbsolutePath(coverImage) : ''}`} className={isNewCover ? styles.roundImage : styles.image} style={{borderRadius: isNewCover ? '' : '3px'}} alt="cover"></LazyLoadImage>
          {completedLabel}
        </>}
        {isNewCover ? label : oldLabel}
        {(progress && progress > 0) ? progressBar : null}
        {isNewCover ? suggestBar : null}
        {programID && gradeLabel && isNewCover ? gradeBar : null}
        {actionContent}
      </div>
      {isNewCover ? bottomView : null}
    </div>
  );
}

export default CoverContent

